/**
 * Is this the production app that nurses run?
 *
 * Decides whether developer tools exist at all: the API environment switcher
 * (tap the logo 5x on the welcome screen, components/DevEnvironmentSheet.tsx)
 * and the `taskhealth://sentry-test` crash link. In the production app neither
 * should exist: the switcher can point the app, and the nurse's login, at any
 * server someone talks her into typing, and the crash link lets any web page or
 * text message crash the app.
 *
 * The answer, in order:
 *   1. A Metro dev session (`__DEV__`) is never production.
 *   2. A build with an EAS Update channel is production when that channel is
 *      "production" (eas.json: production, production-apk). "preview" and
 *      "development" builds keep the tools.
 *   3. No channel (the web PWA at mobile.taskshealth.com; a development build
 *      running an update): production when the API URL baked in at build time
 *      is the production API.
 *
 * Reads only what an old binary already has: the channel comes from the
 * ExpoUpdates native module, which every store build carries (it is what
 * delivers OTAs), looked up without evaluating the expo-updates package, the
 * same way lib/app-lock.ts probes for biometrics. JS only, safe over OTA.
 */
import { requireOptionalNativeModule } from "expo-modules-core";

export type ApiPreset = "production" | "staging" | "local";

/** Which preset an API URL baked in at build time belongs to. */
export function presetForApiUrl(url: string | undefined | null): ApiPreset {
    const u = url ?? "";
    if (u.includes("staging")) {
        return "staging";
    }
    if (u.includes("taskshealth.com")) {
        return "production";
    }
    return "local";
}

export interface BuildFacts {
    isDev: boolean;
    /** EAS Update channel; null or "" when the build has none (web, dev builds). */
    channel: string | null | undefined;
    /** EXPO_PUBLIC_API_URL as baked in at build time. */
    bakedApiUrl: string | null | undefined;
}

/** Pure decision; see the module comment for the order. */
export function isProductionBuildFor(facts: BuildFacts): boolean {
    if (facts.isDev) {
        return false;
    }
    if (typeof facts.channel === "string" && facts.channel !== "") {
        return facts.channel === "production";
    }
    return presetForApiUrl(facts.bakedApiUrl) === "production";
}

function readChannel(): string | null {
    try {
        const mod = requireOptionalNativeModule<{ channel?: unknown }>("ExpoUpdates");
        return typeof mod?.channel === "string" ? mod.channel : null;
    } catch {
        return null;
    }
}

let cached: boolean | null = null;

/** True in the production app; memoized (none of its inputs change while running). */
export function isProductionBuild(): boolean {
    if (cached === null) {
        cached = isProductionBuildFor({
            isDev: typeof __DEV__ !== "undefined" && __DEV__ === true,
            channel: readChannel(),
            // Must stay a literal `process.env.EXPO_PUBLIC_API_URL`: Expo inlines
            // it at build time only in this exact form.
            bakedApiUrl: process.env.EXPO_PUBLIC_API_URL
        });
    }
    return cached;
}

/** Developer tools (environment switcher, Sentry test link) are available. */
export function devToolsEnabled(): boolean {
    return !isProductionBuild();
}
